import i18n from '@/utils/i18n';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import {
  AdyenDropinOnSubmitData,
  toUnsavedPaymentMethodOption,
} from '@/utils/payments';
import { AdyenDropinWrapper } from '@/components/payments/adyen-dropin-wrapper';
import { AlertLevel } from '@/types';
import { Button } from '@/components/button';
import { CheckCaptureContent } from '@/components/payments/check-capture-content';
import {
  CheckSubmitRecovery,
  SubmitCheckDepositResult,
} from '@/hooks/payments/use-submit-check-deposit';
import { CollectPADConsentForm } from '@/components/payments/collect-pad-consent-form';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { CreditRequestBatchLineItem } from '@/contexts/credit-request-context-types';
import { CreditRequestFormContent } from '@/components/credit-request/credit-request-form-content';
import { CreditRequestInvoiceDetailsContent } from '@/components/credit-request/credit-request-invoice-details-content';
import { CreditRequestInvoiceItemsContent } from '@/components/credit-request/credit-request-invoice-items-content';
import { ItemListOption } from '@/contexts/item-context-types';
import { PadAdditionalTerms } from '@/components/payments/pad-additional-terms';
import { PadAgreement } from '@/components/payments/pad-agreement';
import {
  PADConsentFormMarkdown,
  useGetPadConsentFormsForPaymentMethod,
  usePadPaymentFlow,
} from '@/hooks/payments/use-pad-payment-operations';
import { PadContactInformationInputs } from '@/components/payments/pad-contact-information-inputs';
import { PadPaymentInfoInputs } from '@/components/payments/pad-payment-info-inputs';
import { PartialPaymentScreenComponent } from '@/components/payments/partial-payment-screen-component';
import {
  PaymentMethodOption,
  usePaymentsContext,
} from '@/contexts/payments-context';
import { PaymentMethodSelector } from '@/components/payments/payment-method-selector';
import { PaymentMethodType } from '@/enums';
import { PaymentReviewComponent } from '@/components/payments/payment-review-component';
import {
  PaymentSubmissionResult,
  TabCardDetails,
} from '@/components/payments/types';
import { PaymentSuccessComponent } from '@/components/payments/payment-success-action-sheet';
import { SCREEN_PADDING, SCREEN_PADDING_LARGE } from '@/styles';
import {
  selectTotalPriceInMicros,
  useCreditRequestInvoiceStore,
  useCreditRequestInvoiceStoreApi,
} from '@/stores/credit-request-invoice-store';
import { TabCardApplicationContent } from '@/components/payments/tab-card-application-content';
import { ToastContext, useToasts } from '@/contexts/toast-context';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCheckCaptureStore } from '@/stores/check-capture-store';
import { useCreditRequestInvoiceItems } from '@/hooks/credit-requests/use-credit-request-invoice-items';
import { useDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useInitiatePlaid } from '@/hooks/use-initiate-plaid';
import { useKlarnaStore } from '@/stores/use-klarna-store';
import type { KlarnaVariant } from '@/hooks/payments/klarna-types';

export enum PaymentsActionSheetView {
  AddPADForm = 'ADD_PAD_FORM',
  AddPaymentMethod = 'ADD_PAYMENT_METHOD',
  CheckCapture = 'CHECK_CAPTURE',
  CollectPADConsent = 'COLLECT_PAD_CONSENT',
  CreditRequestDetails = 'CREDIT_REQUEST_DETAILS',
  CreditRequestForm = 'CREDIT_REQUEST_FORM',
  CreditRequestItems = 'CREDIT_REQUEST_ITEMS',
  Loading = 'LOADING',
  PADAdditionalTerms = 'PAD_ADDITIONAL_TERMS',
  PADAgreement = 'PAD_AGREEMENT',
  PartialPayment = 'PARTIAL_PAYMENT',
  Review = 'REVIEW',
  SelectPaymentMethod = 'SELECT_PAYMENT_METHOD',
  Success = 'SUCCESS',
  TabCardApplication = 'TAB_CARD_APPLICATION',
}
export interface Props {
  calculatedTransactionFee: number;
  checkNumber?: string;
  creditRequestEnabled?: boolean;
  effectiveSubtotalInMicros: number;
  initialView?: PaymentsActionSheetView;
  isCash?: boolean;
  isDistributorUser?: boolean;
  isKlarnaBusy?: boolean;
  isManualCheck?: boolean;
  isSubmitting: boolean;
  klarnaPaymentMethods?: KlarnaVariant[];
  onChangeCheckNumber?: (value: string) => void;
  onClose: () => void;
  onCreditRequestWorkflowComplete?: (
    amountInMicros: number,
    batchItems: CreditRequestBatchLineItem[],
  ) => void;
  onKlarnaContinue?: () => Promise<boolean>;
  onPartialPaymentChange?: (amountInMicros: number) => void;
  onPartialPaymentClear?: () => void;
  onPaymentSuccess?: () => void;
  onPaymentSuccessAutoPayEnrollment: () => void;
  onRecordCashPayment?: () => void;
  onRecordCheckPayment?: () => void;
  partialPaymentAmountInMicros?: number | null;
  payAmountInMicrosOverride?: number | null;
  refreshQueries: () => void;
  reviewBanner?: (navigation: {
    jumpToTabCardApplication: () => void;
  }) => React.ReactNode;
  selectedOrderInvoiceUUID?: string;
  submitCheckPayment: () => Promise<SubmitCheckDepositResult>;
  submitPayment: () => Promise<PaymentSubmissionResult>;
  subtotalInMicros: number;
  tabCardDetails?: TabCardDetails | null;
  transactionFeeName: string | null;
  walletFooterBanner?: (navigation: {
    jumpToTabCardApplication: () => void;
  }) => React.ReactNode;
}

export const CombinedPaymentsActionSheet: React.FC<Props> = ({
  calculatedTransactionFee,
  checkNumber,
  creditRequestEnabled,
  effectiveSubtotalInMicros,
  initialView,
  isCash,
  isManualCheck,
  isDistributorUser,
  isKlarnaBusy,
  isSubmitting,
  klarnaPaymentMethods,
  onChangeCheckNumber,
  onClose,
  onKlarnaContinue,
  onPartialPaymentChange,
  onPartialPaymentClear,
  onPaymentSuccess,
  onPaymentSuccessAutoPayEnrollment,
  onCreditRequestWorkflowComplete,
  onRecordCashPayment,
  onRecordCheckPayment,
  partialPaymentAmountInMicros,
  payAmountInMicrosOverride,
  refreshQueries,
  reviewBanner,
  selectedOrderInvoiceUUID,
  submitCheckPayment,
  submitPayment,
  subtotalInMicros,
  tabCardDetails,
  transactionFeeName,
  walletFooterBanner,
}) => {
  const {
    currencyCode,
    enabledPaymentMethods,
    plaidEnabled,
    refetchPaymentContext,
    resetContext,
    selectedPaymentMethod,
    setPaymentContextState,
    transactionFeeMap,
  } = usePaymentsContext();
  const { selectedRestaurantRelationship, selectedRestaurantUUID } =
    useAuthenticatedUserContext();
  const { showErrorToast } = useToasts();
  const getPadConsentFormsForPaymentMethod =
    useGetPadConsentFormsForPaymentMethod();
  const initiatePlaid = useInitiatePlaid();
  const [styles] = useStyles();
  const {
    areAllFieldsValid,
    collectConsentForPaymentMethod,
    consentFormsData,
    dispatch,
    fetchConsentAgreementText,
    formState,
    isContactInfoValid,
    isSubmittingPad,
    isLoadingConsentForms,
    handleSubmitPad,
  } = usePadPaymentFlow();
  const isAddingNewPADPaymentMethod = Boolean(consentFormsData);

  const [isCollectingConsent, setIsCollectingConsent] = useState(false);
  const [tabCardPreviousView, setTabCardPreviousView] = useState(
    PaymentsActionSheetView.Review,
  );
  const [combinedConsentFormMarkdown, setCombinedConsentFormMarkdown] =
    useState<PADConsentFormMarkdown | null>(null);
  const [stagedFormItem, setStagedFormItem] = useState<{
    eligibleForCredit: boolean;
    existingBatchLineItem: CreditRequestBatchLineItem | undefined;
    guaranteed: boolean;
    variantPack: ItemListOption;
  } | null>(null);

  const clearCheckSelection = useCheckCaptureStore(
    (s) => s.clearCheckSelection,
  );
  const clearKlarnaSelection = useKlarnaStore((s) => s.clearKlarnaSelection);
  const chooseKlarnaVariant = useKlarnaStore((s) => s.chooseKlarnaVariant);
  const commitCheckSelection = useCheckCaptureStore(
    (s) => s.commitCheckSelection,
  );
  const resetCheckCaptureStore = useCheckCaptureStore((s) => s.reset);
  const clearCheckCaptureImages = useCheckCaptureStore((s) => s.clearImages);
  const checkSelected = useCheckCaptureStore((s) => s.submission.checkSelected);
  const checkCredentials = useCheckCaptureStore((s) => s.credentials);

  const padEnabled = enabledPaymentMethods.includes(PaymentMethodType.PAD);
  const checkCaptureEnabled = useDynamicConfigFlag(
    'jack_henry_checks_config',
    'check_capture_enabled',
  );
  // Mobile-only (MiSnap is a native SDK).
  const showCheckCapture = checkCaptureEnabled && Platform.OS !== 'web';

  const addBatchItem = useCreditRequestInvoiceStore((s) => s.addBatchItem);
  const creditRequestStoreApi = useCreditRequestInvoiceStoreApi();

  const { items: creditRequestItems } = useCreditRequestInvoiceItems({
    orderInvoiceUUID: selectedOrderInvoiceUUID,
    restaurantUUID: selectedRestaurantUUID,
    skip: !creditRequestEnabled,
    supplierUUID: selectedRestaurantRelationship.supplierUUID,
  });

  useEffect(() => {
    if (consentFormsData) {
      setCombinedConsentFormMarkdown(consentFormsData);
    }
  }, [consentFormsData]);

  const canMakePayment =
    checkSelected ||
    isCash ||
    isManualCheck ||
    (selectedPaymentMethod && !isDistributorUser);
  const hasWalletOptions =
    padEnabled ||
    showCheckCapture ||
    Boolean(onRecordCashPayment) ||
    Boolean(onRecordCheckPayment) ||
    Boolean(klarnaPaymentMethods?.length);

  const nextView = canMakePayment
    ? PaymentsActionSheetView.Review
    : hasWalletOptions
      ? PaymentsActionSheetView.SelectPaymentMethod
      : PaymentsActionSheetView.AddPaymentMethod;

  return (
    <CombinedActionSheet
      initialRoute={initialView ?? nextView}
      onClose={(routeKey) => {
        // Prevent closing while payment is being submitted
        if (isSubmitting) {
          return;
        }

        if (routeKey === PaymentsActionSheetView.Success) {
          refreshQueries();
        }

        onClose();
      }}
      onRouteChange={(nextKey, prevKey) => {
        // Discards captured check images when user backs out
        if (
          prevKey === PaymentsActionSheetView.CheckCapture &&
          nextKey === PaymentsActionSheetView.SelectPaymentMethod
        ) {
          clearCheckCaptureImages();
        }
      }}
      renderScene={({ jumpTo, routeKey }) => {
        switch (routeKey) {
          case PaymentsActionSheetView.AddPADForm: {
            return (
              <View>
                <View>
                  <PadPaymentInfoInputs
                    dispatch={dispatch}
                    formState={formState}
                  />
                  <View style={styles.padFormSpacer} />
                  <PadContactInformationInputs
                    dispatch={dispatch}
                    formState={formState}
                  />
                </View>
                <View style={styles.padButtonContainer}>
                  <Button
                    disabled={!areAllFieldsValid}
                    onPress={async () => {
                      setIsCollectingConsent(false);
                      jumpTo(PaymentsActionSheetView.PADAgreement);
                      await fetchConsentAgreementText();
                    }}
                    style={styles.button}
                    trackingData={{ name: 'pad_desktop_form.save_button' }}
                  >
                    {i18n.t('save')}
                  </Button>
                </View>
              </View>
            );
          }
          case PaymentsActionSheetView.AddPaymentMethod: {
            return (
              <AdyenDropinWrapper
                onAddUnsavedMethod={(json: string) => {
                  const adyenData: AdyenDropinOnSubmitData = JSON.parse(json);
                  clearCheckSelection();
                  clearKlarnaSelection();
                  setPaymentContextState({
                    selectedPaymentMethod:
                      toUnsavedPaymentMethodOption(adyenData),
                  });
                  jumpTo(PaymentsActionSheetView.Review);
                }}
                onSaveSuccess={() => jumpTo(PaymentsActionSheetView.Review)}
                transactionFeeMap={transactionFeeMap}
              />
            );
          }
          case PaymentsActionSheetView.CollectPADConsent: {
            return (
              <>
                <CollectPADConsentForm
                  dispatch={dispatch}
                  formState={formState}
                />
                <View style={styles.padButtonContainer}>
                  <Button
                    disabled={!isContactInfoValid || !formState.padBusinessType}
                    onPress={async () => {
                      setIsCollectingConsent(true);
                      jumpTo(PaymentsActionSheetView.PADAgreement);
                      await fetchConsentAgreementText();
                    }}
                    style={styles.button}
                    trackingData={{ name: 'pad_desktop_form.save_button' }}
                  >
                    {i18n.t('save')}
                  </Button>
                </View>
              </>
            );
          }
          case PaymentsActionSheetView.Loading: {
            return (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" />
              </View>
            );
          }
          case PaymentsActionSheetView.PADAgreement: {
            if (isLoadingConsentForms || !combinedConsentFormMarkdown) {
              return (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator />
                </View>
              );
            }
            return (
              <View>
                <View style={styles.padAgreementBoxBorder}>
                  <PadAgreement
                    markdownTemplate={combinedConsentFormMarkdown.padAgreement}
                    onViewAdditionalTerms={() =>
                      jumpTo(PaymentsActionSheetView.PADAdditionalTerms)
                    }
                  />
                </View>
                {isAddingNewPADPaymentMethod ? (
                  <View style={styles.padButtonContainer}>
                    <Button
                      disabled={isSubmittingPad}
                      loading={isSubmittingPad}
                      onPress={async () => {
                        if (isCollectingConsent) {
                          await collectConsentForPaymentMethod(async () => {
                            try {
                              const result = await submitPayment();

                              if (result?.alert_level === AlertLevel.Negative) {
                                showErrorToast(result.alert_message!);
                                refreshQueries();
                                onClose();
                              } else if (
                                result?.alert_level === AlertLevel.Positive
                              ) {
                                jumpTo(PaymentsActionSheetView.Success);
                                resetContext();
                                onPaymentSuccess?.();
                              }
                            } catch (e) {
                              showErrorToast();
                              onClose();
                            }
                          });
                        } else {
                          handleSubmitPad(() =>
                            jumpTo(PaymentsActionSheetView.Review),
                          );
                        }
                      }}
                      style={styles.button}
                      trackingData={{ name: 'pad_agreement.agree_button' }}
                    >
                      {i18n.t('agree')}
                    </Button>
                  </View>
                ) : null}
              </View>
            );
          }
          case PaymentsActionSheetView.PADAdditionalTerms: {
            if (!combinedConsentFormMarkdown) {
              return (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator />
                </View>
              );
            }

            return (
              <View style={styles.padAgreementBoxBorder}>
                <PadAdditionalTerms
                  markdownTemplate={
                    combinedConsentFormMarkdown.padAdditionalTerms
                  }
                />
              </View>
            );
          }
          case PaymentsActionSheetView.PartialPayment: {
            return (
              <PartialPaymentScreenComponent
                currencyCode={currencyCode}
                initialAmountInMicros={partialPaymentAmountInMicros}
                invoiceTotalInMicros={subtotalInMicros}
                onClear={
                  onPartialPaymentClear
                    ? () => {
                        onPartialPaymentClear();
                        jumpTo(PaymentsActionSheetView.Review);
                      }
                    : undefined
                }
                onSave={(amountInMicros) => {
                  onPartialPaymentChange?.(amountInMicros);
                  jumpTo(nextView);
                }}
              />
            );
          }
          case PaymentsActionSheetView.CreditRequestItems: {
            return (
              <CreditRequestInvoiceItemsContent
                items={creditRequestItems}
                onNext={() =>
                  jumpTo(PaymentsActionSheetView.CreditRequestDetails)
                }
                onSelectItem={(
                  variantPack,
                  searchResultItem,
                  existingBatchLineItem,
                ) => {
                  setStagedFormItem({
                    eligibleForCredit: searchResultItem.eligible_for_credit,
                    existingBatchLineItem,
                    guaranteed: searchResultItem.guaranteed,
                    variantPack,
                  });
                  jumpTo(PaymentsActionSheetView.CreditRequestForm);
                }}
              />
            );
          }
          case PaymentsActionSheetView.CreditRequestForm: {
            return (
              <CreditRequestFormContent
                // `stagedFormItem` is guaranteed because it's set before navigating to this route
                eligibleForCredit={stagedFormItem!.eligibleForCredit}
                existingBatchLineItem={stagedFormItem!.existingBatchLineItem}
                guaranteed={stagedFormItem!.guaranteed}
                itemDetail={stagedFormItem!.variantPack}
                multiRequest
                onSuccess={(batchLineItem) => {
                  addBatchItem(batchLineItem);
                  setStagedFormItem(null);
                  jumpTo(PaymentsActionSheetView.CreditRequestItems);
                }}
                orderInvoiceUUID={selectedOrderInvoiceUUID!}
              />
            );
          }
          case PaymentsActionSheetView.CreditRequestDetails: {
            // Validation toast must render above the modal backdrop and the sheet
            // stays open afterward, so subscribe to BaseActionSheet's inner
            // ToastContextProvider here instead of the screen-level one.
            return (
              <ToastContext.Consumer>
                {({ showErrorToast: showInSheetErrorToast }) => (
                  <CreditRequestInvoiceDetailsContent
                    onEmpty={() =>
                      jumpTo(PaymentsActionSheetView.CreditRequestItems)
                    }
                    onSubmit={() => {
                      const state = creditRequestStoreApi.getState();
                      const creditTotalInMicros =
                        selectTotalPriceInMicros(state);
                      const paymentAmount =
                        subtotalInMicros - creditTotalInMicros;
                      if (paymentAmount <= 0) {
                        showInSheetErrorToast(
                          i18n.t('partial_payment.error.zero_payment'),
                        );
                        return;
                      }
                      onCreditRequestWorkflowComplete?.(
                        paymentAmount,
                        state.batchItems,
                      );
                      jumpTo(nextView);
                    }}
                    orderInvoiceUUID={selectedOrderInvoiceUUID!}
                    submitLabel={i18n.t('done')}
                  />
                )}
              </ToastContext.Consumer>
            );
          }
          case PaymentsActionSheetView.Review: {
            return (
              <PaymentReviewComponent
                calculatedTransactionFee={calculatedTransactionFee}
                checkNumber={checkNumber}
                isCash={isCash}
                isKlarnaBusy={isKlarnaBusy}
                isManualCheck={isManualCheck}
                isSubmitting={isSubmitting}
                onChange={() =>
                  jumpTo(PaymentsActionSheetView.SelectPaymentMethod)
                }
                onChangeCheckNumber={onChangeCheckNumber}
                onCollectPADConsent={() => {
                  jumpTo(PaymentsActionSheetView.CollectPADConsent);
                }}
                onKlarnaContinue={
                  onKlarnaContinue
                    ? async () => {
                        const succeeded = await onKlarnaContinue();
                        if (succeeded) {
                          jumpTo(PaymentsActionSheetView.Success);
                        } else {
                          onClose();
                        }
                        return succeeded;
                      }
                    : undefined
                }
                onSubmit={async () => {
                  if (checkSelected && checkCredentials) {
                    try {
                      const result = await submitCheckPayment();
                      if (result.ok) {
                        jumpTo(PaymentsActionSheetView.Success);
                        resetContext();
                        onPaymentSuccess?.();
                        return;
                      }
                      if (result.recovery === CheckSubmitRecovery.START_OVER) {
                        showErrorToast(i18n.t('check_capture.submit_failed'));
                        resetCheckCaptureStore();
                        onClose();
                      } else if (
                        result.recovery === CheckSubmitRecovery.RETRY
                      ) {
                        showErrorToast(result.message);
                        resetCheckCaptureStore();
                        onClose();
                      } else {
                        showErrorToast(result.message);
                      }
                    } catch (e) {
                      showErrorToast();
                    }
                    return;
                  }
                  try {
                    const result = await submitPayment();

                    if (result?.alert_level === AlertLevel.Negative) {
                      showErrorToast(result.alert_message!);
                      refreshQueries();
                      onClose();
                    } else if (result?.alert_level === AlertLevel.Positive) {
                      jumpTo(PaymentsActionSheetView.Success);
                      resetContext();
                      onPaymentSuccess?.();
                    }
                  } catch (e) {
                    showErrorToast();
                    onClose();
                  }
                }}
                onViewPADAgreement={async () => {
                  if (!selectedPaymentMethod) {
                    return;
                  }

                  const { data } = await getPadConsentFormsForPaymentMethod({
                    variables: {
                      paymentMethodUUID: selectedPaymentMethod.uuid,
                      restaurantUUID: selectedRestaurantUUID,
                      supplierUUID: selectedRestaurantRelationship.supplierUUID,
                    },
                  });

                  if (
                    data?.getPadConsentFormsForPaymentMethod?.pad_agreement &&
                    data?.getPadConsentFormsForPaymentMethod
                      ?.pad_additional_terms_and_conditions
                  ) {
                    setCombinedConsentFormMarkdown({
                      padAdditionalTerms:
                        data.getPadConsentFormsForPaymentMethod
                          .pad_additional_terms_and_conditions,
                      padAgreement:
                        data.getPadConsentFormsForPaymentMethod.pad_agreement,
                    });
                  }

                  jumpTo(PaymentsActionSheetView.PADAgreement);
                }}
                payAmountInMicrosOverride={payAmountInMicrosOverride}
                reviewBanner={reviewBanner?.({
                  jumpToTabCardApplication: () => {
                    setTabCardPreviousView(PaymentsActionSheetView.Review);
                    jumpTo(PaymentsActionSheetView.TabCardApplication);
                  },
                })}
                subtotalInMicros={effectiveSubtotalInMicros}
                transactionFeeName={transactionFeeName}
              />
            );
          }
          case PaymentsActionSheetView.SelectPaymentMethod: {
            return (
              <View style={styles.paymentMethodSelector}>
                <PaymentMethodSelector
                  footerBanner={walletFooterBanner?.({
                    jumpToTabCardApplication: () => {
                      setTabCardPreviousView(
                        PaymentsActionSheetView.SelectPaymentMethod,
                      );
                      jumpTo(PaymentsActionSheetView.TabCardApplication);
                    },
                  })}
                  isDistributorUser={isDistributorUser}
                  klarnaPaymentMethods={klarnaPaymentMethods}
                  onAddMethod={() =>
                    jumpTo(PaymentsActionSheetView.AddPaymentMethod)
                  }
                  onAddPAD={() => {
                    jumpTo(PaymentsActionSheetView.AddPADForm);
                  }}
                  onConnectBankAccount={
                    plaidEnabled
                      ? async () => {
                          try {
                            jumpTo(PaymentsActionSheetView.Loading);
                            await initiatePlaid();

                            // Once the browser is closed, refresh the payment
                            // methods in case a new one was added for Plaid
                            await refetchPaymentContext();
                            jumpTo(PaymentsActionSheetView.SelectPaymentMethod);
                          } catch (e) {
                            showErrorToast();
                            jumpTo(PaymentsActionSheetView.SelectPaymentMethod);
                          }
                        }
                      : undefined
                  }
                  onPayByCheck={
                    showCheckCapture
                      ? () => {
                          clearKlarnaSelection();
                          jumpTo(PaymentsActionSheetView.CheckCapture);
                        }
                      : undefined
                  }
                  onRecordCashPayment={
                    onRecordCashPayment
                      ? () => {
                          onRecordCashPayment();
                          jumpTo(PaymentsActionSheetView.Review);
                        }
                      : undefined
                  }
                  onRecordCheckPayment={
                    onRecordCheckPayment
                      ? () => {
                          onRecordCheckPayment();
                          jumpTo(PaymentsActionSheetView.Review);
                        }
                      : undefined
                  }
                  onSelectKlarna={
                    klarnaPaymentMethods?.length
                      ? (variant) => {
                          clearCheckSelection();
                          chooseKlarnaVariant({ variant });
                          jumpTo(PaymentsActionSheetView.Review);
                        }
                      : undefined
                  }
                  onSelectMethod={(paymentMethod?: PaymentMethodOption) => {
                    clearCheckSelection();
                    clearKlarnaSelection();
                    setPaymentContextState({
                      selectedPaymentMethod: paymentMethod,
                    });
                    jumpTo(PaymentsActionSheetView.Review);
                  }}
                />
              </View>
            );
          }
          case PaymentsActionSheetView.CheckCapture: {
            return (
              <CheckCaptureContent
                chatUUID={selectedRestaurantRelationship.chatUUIDs[0]}
                currencyCode={currencyCode}
                onUnavailable={(message) => {
                  showErrorToast(message);
                  resetCheckCaptureStore();
                  jumpTo(PaymentsActionSheetView.SelectPaymentMethod);
                }}
                onUseCheck={() => {
                  commitCheckSelection();
                  jumpTo(PaymentsActionSheetView.Review);
                }}
                subtotalInMicros={effectiveSubtotalInMicros}
              />
            );
          }
          case PaymentsActionSheetView.TabCardApplication: {
            return tabCardDetails ? (
              <TabCardApplicationContent
                onAfterSignUp={onClose}
                tabCardDetails={tabCardDetails}
              />
            ) : null;
          }
          case PaymentsActionSheetView.Success: {
            return (
              <View style={styles.successContainer}>
                <PaymentSuccessComponent
                  onClose={() => {
                    refreshQueries();
                    onClose();
                  }}
                  onPaymentSuccessAutoPayEnrollment={
                    onPaymentSuccessAutoPayEnrollment
                  }
                />
              </View>
            );
          }
          default: {
            return null;
          }
        }
      }}
      routes={{
        [PaymentsActionSheetView.AddPADForm]: {
          previousKey: PaymentsActionSheetView.SelectPaymentMethod,
          title: i18n.t('add_pad_ach'),
        },
        [PaymentsActionSheetView.AddPaymentMethod]: {
          previousKey:
            selectedPaymentMethod &&
            PaymentsActionSheetView.SelectPaymentMethod,
          title: i18n.t('add_payment_method'),
        },
        [PaymentsActionSheetView.CheckCapture]: {
          previousKey: PaymentsActionSheetView.SelectPaymentMethod,
          title: i18n.t('check_capture.title'),
        },
        [PaymentsActionSheetView.CollectPADConsent]: {
          previousKey: PaymentsActionSheetView.Review,
          title: i18n.t('pad.agreement'),
        },
        [PaymentsActionSheetView.CreditRequestDetails]: {
          previousKey: PaymentsActionSheetView.CreditRequestItems,
          title: i18n.t('credit_request.review'),
        },
        [PaymentsActionSheetView.CreditRequestForm]: {
          previousKey: PaymentsActionSheetView.CreditRequestItems,
          title: i18n.t('credit_request'),
        },
        [PaymentsActionSheetView.CreditRequestItems]: {
          previousKey: null,
          title: i18n.t('credit_request.select_items'),
        },
        [PaymentsActionSheetView.Loading]: {
          previousKey: null,
        },
        [PaymentsActionSheetView.PADAgreement]: {
          previousKey: isCollectingConsent
            ? PaymentsActionSheetView.CollectPADConsent
            : isAddingNewPADPaymentMethod
              ? PaymentsActionSheetView.AddPADForm
              : PaymentsActionSheetView.Review,
          title: isCollectingConsent
            ? i18n.t('pad.agreement')
            : i18n.t('add_pad_ach'),
        },
        [PaymentsActionSheetView.PADAdditionalTerms]: {
          previousKey: PaymentsActionSheetView.PADAgreement,
          title: isCollectingConsent
            ? i18n.t('pad.agreement')
            : i18n.t('add_pad_ach'),
        },
        [PaymentsActionSheetView.PartialPayment]: {
          previousKey: null,
          title: i18n.t('partial_payment'),
        },
        [PaymentsActionSheetView.Review]: {
          previousKey: null,
          title: i18n.t('send_payment'),
        },
        [PaymentsActionSheetView.SelectPaymentMethod]: {
          previousKey:
            selectedPaymentMethod || checkSelected
              ? PaymentsActionSheetView.Review
              : null,
          title: i18n.t('select_payment_method'),
        },
        [PaymentsActionSheetView.Success]: {
          previousKey: null,
        },
        [PaymentsActionSheetView.TabCardApplication]: {
          previousKey: tabCardPreviousView,
          title: i18n.t('tab_card_modal.header'),
        },
      }}
    />
  );
};

const useStyles = createStyles((theme) => ({
  button: {
    alignSelf: 'flex-end',
    borderRadius: 8,
    minWidth: 120,
  },
  loadingContainer: {
    paddingBottom: 48,
    paddingTop: 32,
  },
  padAgreementBoxBorder: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: 8,
    borderWidth: 1,
  },
  padButtonContainer: {
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  padFormSpacer: {
    height: 24,
  },
  paymentMethodSelector: {
    marginHorizontal: -SCREEN_PADDING,
  },
  successContainer: {
    marginBottom: SCREEN_PADDING_LARGE,
  },
}));
